import { Text, type TypeTableColumn } from '@thefarmersfront/kitchen';

import styles from '~/components/receive/ReceiveStatusDetailModal/ReceiveStatusDetailModal.module.scss';

import { BadgeCell, TextButtonCell } from '~/components/common';
import { ReceiveStatusQtyDetailCell } from '~/components/receive';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT } from '~/constants';
import { type TypeReceiveStatusDetailStatus } from '~/constants/receive';

import { RECEIVE_STATUS_DETAIL_STATUS_COLOR_MAP } from './receiveBadgeColorMaps';

import type { TypeReceiveStatusDetailItem } from '~/types/receive.types';

/** 수량상세 1건을 1행으로 펼친 행. 수량상세가 없는 상품도 한 행(detail 이 null)으로 둔다. */
export type TypeReceiveStatusDetailRow = TypeReceiveStatusDetailItem & {
  /** 상품(부모) 순번. 부모 컬럼 병합 경계로 쓴다. */
  itemNo: number;
  detail: TypeReceiveStatusDetailItem['details'][number] | null;
  rowId: string;
};

export const flattenReceiveStatusDetailItems = (items: TypeReceiveStatusDetailItem[]): TypeReceiveStatusDetailRow[] =>
  items.flatMap((item, index) => {
    const details = item.details.length > 0 ? item.details : [null];
    return details.map((detail, detailIndex) => ({
      ...item,
      itemNo: index + 1,
      detail,
      rowId: `${item.requestItemId}-${detailIndex}`,
    }));
  });

/**
 * [입고관리 > 입고현황] 입고 현황 상세 모달 > 입고 처리 상세 테이블
 *
 * 수량상세를 행으로 펼치고 상품 단위 컬럼은 상품 순번(No) 경계 안에서 병합한다.
 */
const getReceiveStatusDetailModalColumnDefs = ({
  hasPermissionNFA,
  onClickViewIssueImage,
}: {
  hasPermissionNFA: boolean;
  onClickViewIssueImage: (requestItemId: number) => Promise<void>;
}) => {
  const columnDefs: TypeTableColumn<TypeReceiveStatusDetailRow>[] = [
    {
      accessorKey: 'itemNo',
      header: 'No',
      size: 72,
      meta: { rowSpan: true, rowSpanOrder: 1 },
    },
    {
      accessorKey: 'status',
      header: '작업상태',
      size: 92,
      cell: ({ getValue }) => (
        <BadgeCell
          value={getValue<TypeReceiveStatusDetailStatus>()}
          colorMap={RECEIVE_STATUS_DETAIL_STATUS_COLOR_MAP}
          variant="light"
          status
        />
      ),
      meta: { rowSpan: true },
    },
    {
      accessorKey: 'goodsCode',
      header: '상품코드',
      size: 140,
      meta: { rowSpan: true },
    },
    ...(hasPermissionNFA
      ? [
          {
            accessorKey: 'nsid',
            header: 'NSID',
            size: 180,
            meta: { rowSpan: true },
          } satisfies TypeTableColumn<TypeReceiveStatusDetailRow>,
        ]
      : []),
    {
      accessorKey: 'goodsName',
      header: '상품명',
      size: 240,
      meta: { rowSpan: true },
    },
    {
      accessorKey: 'requestedQty',
      header: '요청수량',
      size: 80,
      cell: ({ getValue }) => {
        const value = getValue<TypeReceiveStatusDetailItem['requestedQty']>();
        return value ? formatNumber(value) : '-';
      },
      meta: { rowSpan: true, cellStyle: { textAlign: 'right' } },
    },
    {
      id: 'detailQty',
      header: () => (
        <div className={styles['receive-status-header-qty']}>
          <Text element="span" size="large" weight="semi-bold" color="success">
            정상
          </Text>
          /
          <Text element="span" size="large" weight="semi-bold" color="error">
            문제
          </Text>
          수량
        </div>
      ),
      meta: {
        headerTooltip: {
          description:
            '정상수량 : 검수를 통과한 입고 수량입니다.\n문제수량 : 검수 시 발견된 문제내용으로 인해\n입고할 수 없는 수량입니다.',
        },
      },
      size: 124,
      cell: ({ row }) => <ReceiveStatusQtyDetailCell detail={row.original.detail} />,
    },
    {
      id: 'detailReason',
      accessorFn: (row) => row.detail?.reason,
      header: '문제사유',
      size: 220,
    },
    {
      id: 'detailReceivedDate',
      accessorFn: (row) => row.detail?.receivedDate,
      header: '입고확정일시',
      meta: { headerTooltip: { description: '입고확정일시에 확정된\n입고 수량만큼 재고로 전환됩니다.' } },
      cell: ({ getValue }) => formatDate(getValue<string | undefined>(), DATE_FORMAT.DATE_TIME) ?? '-',
      size: 160,
    },
    {
      accessorKey: 'hasIssueImages',
      header: '이슈 이미지',
      size: 92,
      cell: ({ getValue, row }) => (
        <TextButtonCell
          value={getValue<boolean>()}
          variant="normal"
          onClick={() => {
            onClickViewIssueImage(row.original.requestItemId);
          }}
        >
          보기
        </TextButtonCell>
      ),
      meta: { rowSpan: true },
    },
  ];

  return { columnDefs };
};

export default getReceiveStatusDetailModalColumnDefs;
